import React, { useMemo } from 'react';
import { Routes } from 'react-router';
import { BonusCodePage } from "@components/pages/consumer/bonuses";
import { ConsumerShortNewsListPage, EditShortNewsPage, AddShortNewsPage } from "@components/pages/consumer/shortNews";
import { ConsumerMarketCategoryList, ConsumerMarketAddCategory, ConsumerMarketEditCategory, ConsumerMarketItemsList, ConsumerMarketAddItem, ConsumerMarketEditItem } from "@components/consumer/market";
import { ConsumerPaymentsList, ConsumerPayment } from "@components/consumer/payments";
import ConsumerDashboard from '@components/pages/consumer/dashboard';
import ConsumerUserListPage from '@components/pages/consumer/users/consumerUserListPage';
import ConsumerUserPage from '@components/pages/consumer/users/consumerUserPage';
import ConsumerTicketsPage from '@components/pages/consumer/support/consumerTicketsPage';
import ConsumerTicketPage from '@components/pages/consumer/support/consumerTicketPage';
import { RouterItem } from '@interfaces/control-panel';
import { useAuth, useAllRoles } from '@services/hooks';
import { childRoutes } from './routes';
import {BonusListPage, CreatePaymentBonusPage, EditPaymentBonusPage} from '@components/pages/consumer/payments/bonuses';
import { ItemsList } from '@components/consumer/items';
import { AddItemPage, EditItemPage } from '@components/pages/consumer/items';
import {AddDailyGiftPage, ConsumerDailyGiftListPage, EditDailyGiftPage, AddGiftGroupPage, EditGiftGroupPage, GiftGroupsListPage} from '@components/pages/consumer/daily';
import { AddGoalForm, AddConditionForm, AddGoalRewardForm } from '@components/consumer/goals';
import { GoalsListPage, EditGoalPage, EditGoalConditionPage, EditGoalRewardPage } from '@components/pages/consumer/goals';
import { GoalGroupsListPage, AddGoalGroupPage, EditGoalGroupPage } from '@components/pages/consumer/goalGroups';
import { AddRefRewardPage, AddRefTargetPage, EditRefPage, EditRefRewardPage, EditRefTargetPage, RefsListPage } from '@components/pages/consumer/refs';
import {MainLayout} from "@components/falcon";
import {AddRefForm} from "@components/consumer/refs";
import {AddBonusCodeForm, BonusCodesList} from "@components/consumer/bonuses";

const consumerRoutes: RouterItem = {
    path: '/',
    name: 'Управление',
    needAuth: true,
    authRole: 'consumer',
    component: ConsumerDashboard,
    children: [
        {
            path: '/users',
            name: 'Пользователи',
            authRole: 'consumer.users.view',
            component: ConsumerUserListPage,
            children: [
                {
                    path: '/users/:id',
                    name: 'Пользователь',
                    code: 'consumer.users.current.name',
                    component: ConsumerUserPage
                }
            ]
        },
        {
            path: '/payments',
            name: 'Платежи',
            authRole: 'consumer.payments.view',
            component: ConsumerPaymentsList,
            children: [
                {
                    path: '/payments/bonuses',
                    name: 'Бонусы',
                    authRole: 'consumer.payments.bonuses.view',
                    component: BonusListPage,
                    children: [
                        {
                            path: '/payments/bonuses/new/',
                            name: 'Создание бонуса',
                            authRole: 'consumer.payments.bonuses.view',
                            component: CreatePaymentBonusPage
                        },
                        {
                            path: '/payments/bonuses/:bonus_id/',
                            name: 'Бонус',
                            authRole: 'consumer.payments.bonuses.view',
                            code: 'consumer.payments.bonuses.current.name',
                            component: EditPaymentBonusPage,
                        }
                    ]
                },
                {
                    path: '/payments/:payment_id',
                    name: 'Платеж',
                    code: 'consumer.payments.current.name',
                    component: ConsumerPayment
                }
            ]
        },
        {
            path: '/support/',
            name: 'Поддержка',
            authRole: 'consumer.support',
            component: ConsumerTicketsPage,
            children: [
                {
                    path: '/support/:id',
                    name: 'Заявка',
                    exact: false,
                    component: ConsumerTicketPage
                }
            ]
        },
        {
            path: '/bonuses/',
            name: 'Бонусные коды',
            authRole: 'consumer.bonus.view',
            component: BonusCodesList,
            children: [
                {
                    path: '/bonuses/create/',
                    name: 'Создание бонусного кода',
                    authRole: 'consumer.bonus.edit',
                    component: AddBonusCodeForm,
                },
                {
                    path: '/bonuses/:code_id/',
                    name: 'Бонусный код',
                    authRole: 'consumer.bonus.view',
                    code: 'consumer.bonus.current.name',
                    component: BonusCodePage,
                }
            ]
        },
        {
            path: '/shortnews/',
            name: 'Короткие новости',
            authRole: 'consumer.shortnews.view',
            component: ConsumerShortNewsListPage,
            children: [
                {
                    path: '/shortnews/add/',
                    name: 'Добавление новости',
                    authRole: 'consumer.shortnews.edit',
                    component: AddShortNewsPage
                },
                {
                    path: '/shortnews/:id/',
                    name: 'Редактирование новости',
                    code: 'consumer.shortnews.current.name',
                    component: EditShortNewsPage
                }
            ]
        },
        {
            path: '/item/',
            name: 'Предметы',
            authRole: 'consumer.items.view',
            component: ItemsList,
            children: [
                {
                    path: '/item/add/',
                    name: 'Добавить предмет',
                    authRole: 'consumer.items.edit',
                    component: AddItemPage
                },
                {
                    path: '/item/:item_id/',
                    name: 'Редактирование предмета',
                    code: 'consumer.item.current.name',
                    component: EditItemPage
                }
            ]
        },
        {
            path: '/market/',
            name: 'Магазин',
            authRole: 'consumer.items.shop.view',
            children: [
                {
                    path: '/market/category/',
                    name: 'Категории',
                    component: ConsumerMarketCategoryList,
                    children: [
                        {
                            path: '/market/category/add/',
                            name: 'Добавить категорию',
                            component: ConsumerMarketAddCategory,
                            authRole: 'consumer.items.shop.edit'
                        },
                        {
                            path: '/market/category/:category_id/',
                            name: 'Редактирование категории',
                            code: 'consumer.market.category.current.name',
                            component: ConsumerMarketEditCategory
                        }
                    ]
                },
                {
                    path: '/market/item/',
                    name: 'Предметы',
                    component: ConsumerMarketItemsList,
                    children: [
                        {
                            path: '/market/item/add/',
                            name: 'Добавление предмета',
                            component: ConsumerMarketAddItem,
                            authRole: 'consumer.items.shop.edit'
                        },
                        {
                            path: '/market/item/:item_id/',
                            name: 'Редактирование предмета',
                            code: 'consumer.market.item.current.name',
                            component: ConsumerMarketEditItem
                        }
                    ]
                }
            ]
        },
        {
            path: '/daily/',
            name: 'Бонусы за вход',
            authRole: 'consumer.daily.view',
            component: ConsumerDailyGiftListPage,
            children: [
                {
                    path: '/daily/groups/',
                    name: 'Группы',
                    authRole: 'consumer.daily.view',
                    component: GiftGroupsListPage,
                    children: [
                        {
                            path: '/daily/groups/add/',
                            name: 'Создание группы',
                            authRole: 'consumer.daily.edit',
                            component: AddGiftGroupPage
                        },
                        {
                            path: '/daily/groups/:group_id/',
                            name: 'Редактирование группы',
                            code: 'consumer.daily.groups.current.name',
                            component: EditGiftGroupPage
                        }
                    ]
                },
                {
                    path: '/daily/add/',
                    name: 'Создание подарка',
                    authRole: 'consumer.daily.edit',
                    component: AddDailyGiftPage
                },
                {
                    path: '/daily/:gift_id/',
                    name: 'Редактирование подарка',
                    code: 'consumer.daily.current.name',
                    component: EditDailyGiftPage
                }
            ]
        },
        {
            path: '/goals/',
            name: 'Достижения',
            authRole: 'consumer.goal.view',
            component: GoalsListPage,
            children: [
                {
                    path: '/goals/groups/',
                    name: 'Группы',
                    authRole: 'consumer.goal.view',
                    component: GoalGroupsListPage,
                    children: [
                        {
                            path: '/goals/groups/add/',
                            name: 'Создание группы',
                            authRole: 'consumer.goal.edit',
                            component: AddGoalGroupPage
                        },
                        {
                            path: '/goals/groups/:group_id/',
                            name: 'Редактирование группы',
                            code: 'consumer.goals.groups.current.name',
                            component: EditGoalGroupPage
                        }
                    ]
                },
                {
                    path: '/goals/add/',
                    name: 'Создание достижения',
                    authRole: 'consumer.goal.edit',
                    component: AddGoalForm
                },
                {
                    path: '/goals/:goal_id/*',
                    name: 'Редактирование достижения',
                    code: 'consumer.goals.current.name',
                    component: EditGoalPage,
                    children: [
                        {
                            path: '/goals/:goal_id/conditions/',
                            name: 'Условия',
                            code: 'consumer.goals.conditions',
                            useLink: true,
                            children: [
                                {
                                    path: '/goals/:goal_id/conditions/add/',
                                    name: 'Создание условия',
                                    authRole: 'consumer.goal.edit',
                                    component: AddConditionForm
                                },
                                {
                                    path: '/goals/:goal_id/conditions/:condition_id/',
                                    name: 'Редактирование условия',
                                    code: 'consumer.goals.condition.current.name',
                                    component: EditGoalConditionPage
                                }
                            ]
                        },
                        {
                            path: '/goals/:goal_id/rewards/',
                            name: 'Награды',
                            code: 'consumer.goals.rewards',
                            useLink: true,
                            children: [
                                {
                                    path: '/goals/:goal_id/rewards/add/',
                                    name: 'Создание награды',
                                    authRole: 'consumer.goal.edit',
                                    component: AddGoalRewardForm
                                },
                                {
                                    path: '/goals/:goal_id/rewards/:reward_id/',
                                    name: 'Редактирование награды',
                                    code: 'consumer.goals.reward.current.name',
                                    component: EditGoalRewardPage
                                }
                            ]
                        }
                    ]
                }
            ]
        },
        {
            name: 'Реферальная система',
            path: '/refs/',
            authRole: 'consumer.ref.view',
            component: RefsListPage,
            children: [
                {
                    path: '/refs/add/',
                    name: 'Создание реферальной награды',
                    authRole: 'consumer.goal.edit',
                    component: AddRefForm
                },
                {
                    path: '/refs/:ref_id/*',
                    name: 'Редактирование реферальной награды',
                    code: 'consumer.refs.current.name',
                    component: EditRefPage,
                    children: [
                        {
                            path: '/refs/:ref_id/targets/',
                            name: 'Условия',
                            code: 'consumer.refs.targets',
                            useLink: true,
                            children: [
                                {
                                    path: '/refs/:ref_id/targets/add/',
                                    name: 'Создание условия',
                                    authRole: 'consumer.ref.edit',
                                    component: AddRefTargetPage
                                },
                                {
                                    path: '/refs/:ref_id/targets/:target_id/',
                                    name: 'Редактирование условия',
                                    code: 'consumer.refs.target.current.name',
                                    component: EditRefTargetPage
                                }
                            ]
                        },
                        {
                            path: '/refs/:ref_id/rewards/',
                            name: 'Награды',
                            code: 'consumer.refs.rewards',
                            useLink: true,
                            children: [
                                {
                                    path: '/refs/:ref_id/rewards/add/',
                                    name: 'Создание награды',
                                    authRole: 'consumer.ref.edit',
                                    component: AddRefRewardPage
                                },
                                {
                                    path: '/refs/:ref_id/rewards/:reward_id/',
                                    name: 'Редактирование награды',
                                    code: 'consumer.refs.reward.current.name',
                                    component: EditRefRewardPage
                                }
                            ]
                        }
                    ]
                }
            ]
        }
    ]
};

const ConsumerRoutes: React.FC = () => {

    const { isAuthed } = useAuth();
    const roles = useAllRoles();

    const childs = useMemo(() => childRoutes(consumerRoutes, roles, isAuthed), [isAuthed, roles]);

    if (childs.length === 0) {
        return null;
    }

    return (
        <MainLayout>
            <Routes>
                {childs}
            </Routes>
        </MainLayout>
    )
}

export default ConsumerRoutes;